import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Info, Pencil, ShieldAlert, Check, Paperclip, Download, X, FileText } from "lucide-react";
import imageCompression from "browser-image-compression";
import { api, fmtError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, L } from "@/constants/labels";

const ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf,.heic,.heif";
const MAX_UPLOAD_MB = 20;

/**
 * Vignette d'attachment fetchée via api.get (Authorization header envoyé),
 * évite le 401 sur `<img src={signed_url}>` où l'auth JWT header n'est pas
 * transmis par le navigateur pour les balises `<img>`.
 */
function AttachmentThumb({ url, alt, onOpen, testId }) {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let created = null;
    const rel = url?.startsWith("/api/") ? url.slice(4) : url;
    (async () => {
      try {
        const { data } = await api.get(rel, { responseType: "blob" });
        if (cancelled) return;
        created = window.URL.createObjectURL(data);
        setBlobUrl(created);
      } catch (e) { /* silencieux, l'utilisateur peut ouvrir en plein via bouton */ }
    })();
    return () => {
      cancelled = true;
      if (created) window.URL.revokeObjectURL(created);
    };
  }, [url]);
  return (
    <button onClick={onOpen} className="block" title={alt} data-testid={testId}>
      {blobUrl ? (
        <img src={blobUrl} alt={alt} className="w-24 h-24 object-cover rounded border border-white/20" />
      ) : (
        <div className="w-24 h-24 rounded border border-white/20 bg-white/10 animate-pulse" />
      )}
    </button>
  );
}

async function compressIfImage(file) {
  // On ne compresse pas les HEIC (le backend s'en charge) ni les PDF
  const name = (file.name || "").toLowerCase();
  const isHeic = name.endsWith(".heic") || name.endsWith(".heif") || (file.type || "").includes("heic") || (file.type || "").includes("heif");
  if (isHeic || (file.type || "").startsWith("application/pdf")) return file;
  if (!(file.type || "").startsWith("image/")) return file;
  try {
    return await imageCompression(file, {
      maxSizeMB: 1.5, maxWidthOrHeight: 1920, useWebWorker: true,
      initialQuality: 0.82, fileType: "image/jpeg",
    });
  } catch (e) {
    // En cas d'échec de compression, on envoie l'original
    return file;
  }
}

export default function MessagesMission() {
  const { missionId } = useParams();
  const { user } = useAuth();
  const [thread, setThread] = useState({ participants: [], messages: [], mission_statut: null });
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null); // {url, filename}
  const fileInputRef = useRef(null);
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/missions/${missionId}/messages`);
      setThread(data);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }), 30);
    } catch (err) { toast.error(fmtError(err)); }
  }, [missionId]);

  useEffect(() => { load(); }, [load]);

  // P3 — Heartbeat de présence : signale au moteur que l'utilisateur est
  // actif sur cette page. Le worker annule les notifications groupées si
  // ce document date de moins de 180 s (voir notif_engine.HEARTBEAT_TTL_SEC).
  // Cadence 120 s (le TTL de 180 s garde une marge d'un tour de worker).
  useEffect(() => {
    if (!missionId) return;
    let alive = true;
    const ctx = `mission:${missionId}`;
    const beat = () => {
      api.post(`/presence/heartbeat`, { context: ctx }).catch(() => {
        // best-effort — ne bloque jamais la messagerie
      });
    };
    beat();  // ping immédiat à l'ouverture
    const id = setInterval(() => { if (alive) beat(); }, 120_000);
    // Ping supplémentaire quand l'onglet redevient visible (utile après un
    // retour d'inactivité longue — l'utilisateur est de nouveau devant sa
    // page mais l'intervalle 120 s peut le rater d'une salve).
    const onVis = () => { if (alive && document.visibilityState === "visible") beat(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      alive = false;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [missionId]);

  const sendText = async (e) => {
    e?.preventDefault();
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      await api.post(`/missions/${missionId}/messages`, { body: text });
      setText("");
      await load();
    } catch (err) { toast.error(fmtError(err)); }
    finally { setBusy(false); }
  };

  const saveEdit = async () => {
    if (!editing || !editing.body.trim()) return;
    setBusy(true);
    try {
      await api.patch(`/missions/${missionId}/messages/${editing.id}`, { body: editing.body });
      setEditing(null);
      await load();
    } catch (err) { toast.error(fmtError(err)); }
    finally { setBusy(false); }
  };

  const handleFilesPicked = async (fileList) => {
    if (!fileList || !fileList.length) return;
    // On envoie d'abord un message "porteur" pour attacher — sans corps, on
    // impose au moins un caractère. Pour éviter cette gymnastique, si l'input
    // texte est rempli on l'utilise, sinon on demande à l'user.
    let body = text.trim();
    if (!body) body = "📎 Pièce jointe";
    setBusy(true);
    try {
      const { data: msg } = await api.post(`/missions/${missionId}/messages`, { body });
      for (const rawFile of Array.from(fileList)) {
        if (rawFile.size > MAX_UPLOAD_MB * 1024 * 1024) {
          toast.error(`Fichier trop lourd (max ${MAX_UPLOAD_MB} Mo) : ${rawFile.name}`);
          continue;
        }
        const file = await compressIfImage(rawFile);
        const fd = new FormData();
        fd.append("file", file, file.name || rawFile.name);
        try {
          await api.post(`/missions/${missionId}/messages/${msg.id}/attachments`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (err) {
          toast.error(`Échec envoi ${rawFile.name} : ${fmtError(err)}`);
        }
      }
      setText("");
      await load();
    } catch (err) { toast.error(fmtError(err)); }
    finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const exportPdf = async () => {
    try {
      const { data } = await api.get(`/missions/${missionId}/messages/export.pdf`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `chat-mission-${missionId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
    } catch (err) { toast.error(fmtError(err)); }
  };

  const openAttachment = async (att) => {
    // signed_url = /api/chat-files/... → axios prefix /api → strip /api
    const rel = att.signed_url.startsWith("/api/") ? att.signed_url.slice(4) : att.signed_url;
    try {
      const { data, headers } = await api.get(rel, { responseType: "blob" });
      const url = window.URL.createObjectURL(data);
      if ((headers["content-type"] || "").startsWith("image/")) {
        setPreview({ url, filename: att.filename_stored });
      } else {
        // PDF → nouvel onglet
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch (err) { toast.error(fmtError(err)); }
  };

  const isMasking = thread.mission_statut === "proposee";
  const canEditMsg = (m) => m.from_user_id === user.id
    && (Date.now() - new Date(m.created_at).getTime()) <= 15 * 60 * 1000;
  const readByOthers = (m) =>
    (m.read_by || []).filter((r) => r.user_id !== m.from_user_id && r.user_id !== user.id);

  return (
    <div>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <h1 className="text-2xl md:text-3xl font-semibold text-[#1F2933]">Messagerie chantier</h1>
        <Link to="/app/missions" className="btn-ghost text-xs" data-testid="back-to-missions">← Missions</Link>
        <button
          className="btn-outline text-xs inline-flex items-center gap-1 ml-auto"
          onClick={exportPdf}
          disabled={busy || (thread.messages || []).length === 0}
          data-testid="chat-export-pdf"
        ><Download size={12} /> Exporter en PDF</button>
      </div>

      {isMasking && (
        <div className="card p-3 mb-3 flex items-start gap-2 bg-[#FFF7E6] border-[#F0B429]" data-testid="chat-masking-banner">
          <ShieldAlert size={16} className="text-[#B77D00] mt-0.5" />
          <div className="text-sm text-[#1F2933]">
            <div className="font-semibold">Coordonnées masquées jusqu'à l'attribution</div>
            <div className="text-[#3E4C59]">
              Tant que la mission n'est pas acceptée, les numéros de téléphone et adresses e-mail sont
              automatiquement remplacés par « [coordonnées masquées] ». Le masquage s'arrête au passage
              en statut « acceptée » — les nouveaux messages seront alors envoyés en clair.
            </div>
          </div>
        </div>
      )}

      <div className="card flex flex-col min-h-[60vh]" data-testid="mission-chat-panel">
        <div className="p-2 border-b border-[#E4E7EB] flex items-center gap-2 text-xs text-[#3E4C59]">
          <Info size={12} />
          <span>{thread.participants.length} participant(s)</span>
          <span>· statut mission : <b>{thread.mission_statut ?? "?"}</b></span>
        </div>
        <div className="flex-1 p-3 overflow-y-auto space-y-2" data-testid="chat-messages-list">
          {thread.messages.length === 0 && (
            <div className="text-sm text-[#3E4C59]">Aucun message dans ce fil.</div>
          )}
          {thread.messages.map((m) => {
            const mine = m.from_user_id === user.id;
            const others = readByOthers(m);
            const editable = canEditMsg(m);
            const isEditing = editing?.id === m.id;
            const atts = m.attachments || [];
            return (
              <div
                key={m.id}
                className={`max-w-[80%] px-3 py-2 rounded-[4px] text-sm ${mine ? "ml-auto bg-[#1B3A5C] text-white" : "bg-[#F5F6F7] text-[#1F2933]"}`}
                data-testid={`chat-msg-${m.id}`}
              >
                {isEditing ? (
                  <>
                    <textarea
                      className="input w-full text-[#1F2933]"
                      rows={3}
                      value={editing.body}
                      onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                      data-testid={`chat-edit-input-${m.id}`}
                    />
                    <div className="mt-2 flex gap-2">
                      <button className="btn-accent text-xs" onClick={saveEdit} disabled={busy} data-testid={`chat-edit-save-${m.id}`}>Enregistrer</button>
                      <button className="btn-ghost text-xs" onClick={() => setEditing(null)} data-testid={`chat-edit-cancel-${m.id}`}>Annuler</button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="whitespace-pre-wrap">{m.body}</div>
                    {atts.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2" data-testid={`chat-atts-${m.id}`}>
                        {atts.map((a) => (
                          a.is_image ? (
                            <AttachmentThumb
                              key={a.id}
                              url={a.thumb_url || a.signed_url}
                              alt={a.filename_stored}
                              onOpen={() => openAttachment(a)}
                              testId={`chat-att-thumb-${a.id}`}
                            />
                          ) : (
                            <button
                              key={a.id}
                              onClick={() => openAttachment(a)}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${mine ? "bg-white/10" : "bg-white border border-[#E4E7EB]"}`}
                              data-testid={`chat-att-pdf-${a.id}`}
                            ><FileText size={12} /> {a.filename_stored}</button>
                          )
                        ))}
                      </div>
                    )}
                    <div className={`text-[10px] mt-1 flex items-center gap-2 ${mine ? "text-white/70" : "text-[#3E4C59]"}`}>
                      <span>{fmtDate(m.created_at)}</span>
                      {m.edited_at && <span title={`Édité ${fmtDate(m.edited_at)}`}>· édité</span>}
                      {mine && others.length > 0 && (
                        <span className="flex items-center gap-1" title={`Vu par ${others.length}`}>
                          <Check size={10} /> vu
                        </span>
                      )}
                      {editable && mine && (
                        <button
                          className="ml-2 underline decoration-dotted"
                          onClick={() => setEditing({ id: m.id, body: m.body })}
                          data-testid={`chat-edit-btn-${m.id}`}
                        ><Pencil size={10} className="inline" /> Modifier</button>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>
        <form onSubmit={sendText} className="border-t border-[#E4E7EB] p-2 flex gap-2 items-end" data-testid="chat-form">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPT}
            style={{ display: "none" }}
            onChange={(e) => handleFilesPicked(e.target.files)}
            data-testid="chat-file-input"
          />
          <button
            type="button"
            className="btn-ghost"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
            title="Joindre une photo ou un PDF"
            data-testid="chat-file-pick"
          ><Paperclip size={16} /></button>
          <textarea
            className="input flex-1"
            rows={2}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendText(e); } }}
            placeholder="Écrire un message…  (Entrée = envoyer, Maj+Entrée = ligne)"
            data-testid="chat-msg-input"
          />
          <button className="btn-primary" disabled={!text.trim() || busy} data-testid="chat-msg-send">{L.common.submit}</button>
        </form>
      </div>

      {preview && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => { window.URL.revokeObjectURL(preview.url); setPreview(null); }}
          data-testid="chat-att-preview"
        >
          <button className="absolute top-4 right-4 text-white" onClick={(e) => { e.stopPropagation(); window.URL.revokeObjectURL(preview.url); setPreview(null); }}>
            <X size={28} />
          </button>
          <img src={preview.url} alt={preview.filename} className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </div>
  );
}
